import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { auth } from './firebase';
import { shouldLogVerbose } from '../utils/env';

// Create axios instance
const apiBaseURL = import.meta.env.VITE_API_URL;
if (!apiBaseURL) {
  console.error('⚠️ VITE_API_URL is not set! API calls will fail.');
  console.error('Current env:', import.meta.env);
}
// Ensure baseURL includes /api (but don't add it twice)
let baseURL = apiBaseURL || 'https://ride-connect-backend-git-main-ro-garcias-projects.vercel.app';
// Remove trailing slash if present
baseURL = baseURL.trim().replace(/\/+$/, '');
// CRITICAL: Ensure URL has protocol (https://) - if missing, add it
// Without protocol, axios treats it as a relative URL and requests go to frontend domain
if (!baseURL.match(/^https?:\/\//i)) {
  baseURL = `https://${baseURL}`;
  console.warn('⚠️ VITE_API_URL missing protocol, added https://:', baseURL);
}
// Remove /api if it's already there (to avoid double /api/api)
// Use case-insensitive match and handle both /api and /API
baseURL = baseURL.replace(/\/api\/?$/i, '');
// Now add /api
baseURL = `${baseURL}/api`;

// Log API URL setup in development and staging
if (shouldLogVerbose()) {
  console.log('🔗 Admin Panel API Base URL:', baseURL);
  console.log('🔗 Original VITE_API_URL:', apiBaseURL || 'not set');
}

const api = axios.create({
  baseURL,
});

// Track if we're currently refreshing the token to avoid multiple refreshes
let isRefreshingToken = false;
let tokenRefreshPromise: Promise<string> | null = null;

/**
 * Get the Firebase auth token, using cached version unless expired.
 * Only force refresh on 401 errors or when token is expired.
 */
async function getAuthToken(forceRefresh = false): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;

  try {
    // If already refreshing, wait for that promise
    if (isRefreshingToken && tokenRefreshPromise) {
      return tokenRefreshPromise;
    }

    if (forceRefresh) {
      isRefreshingToken = true;
      tokenRefreshPromise = user.getIdToken(true);
      const token = await tokenRefreshPromise;
      isRefreshingToken = false;
      tokenRefreshPromise = null;
      return token;
    }

    // Use cached token (Firebase SDK handles expiration internally)
    return await user.getIdToken(false);
  } catch (error) {
    isRefreshingToken = false;
    tokenRefreshPromise = null;
    throw error;
  }
}

// Add request interceptor to include Firebase token
api.interceptors.request.use(
  async (config) => {
    const user = auth.currentUser;
    if (user) {
      try {
        // Use cached token - don't force refresh on every request
        const token = await getAuthToken(false);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        // Log token usage for debugging (in dev and staging)
        if (shouldLogVerbose()) {
          console.log('🔑 Using Firebase token for API request:', {
            email: user.email,
            uid: user.uid,
            endpoint: config.url
          });
        }
      } catch (error) {
        console.error('Failed to get Firebase token:', error);
        // Don't sign out here - let the 401 handler deal with it
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Queue for requests that failed due to 401 and are waiting for token refresh
let failedQueue: Array<{
  resolve: (config: InternalAxiosRequestConfig) => void;
  reject: (error: AxiosError) => void;
  config: InternalAxiosRequestConfig;
}> = [];

function processQueue(error: AxiosError | null) {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else {
      resolve(config);
    }
  });
  failedQueue = [];
}

// Add response interceptor for error handling with retry on 401
api.interceptors.response.use(
  (response) => {
    // Validate that response is JSON (not HTML error page)
    const contentType = response.headers['content-type'] || '';
    if (!contentType.includes('application/json')) {
      console.error('🚨 API returned non-JSON response:', {
        contentType,
        status: response.status,
        url: response.config?.url,
        dataPreview: typeof response.data === 'string' ? response.data.substring(0, 200) : 'Non-string data'
      });
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    // Handle 401 errors with token refresh and retry
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshingToken) {
        // Wait for the ongoing token refresh
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: originalRequest });
        }).then((config) => api(config));
      }

      try {
        const user = auth.currentUser;
        if (!user) {
          // No user, redirect to login
          window.location.href = '/store';
          return Promise.reject(error);
        }

        // Force refresh the token
        const newToken = await getAuthToken(true);
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null);
          return api(originalRequest);
        }
      } catch (refreshError) {
        processQueue(error);
        // Token refresh failed, clear auth and redirect
        console.error('Token refresh failed:', refreshError);
        await auth.signOut();
        window.location.href = '/store';
        return Promise.reject(error);
      }
    }

    // Log non-401 errors for debugging
    if (error.response && error.response.status !== 401) {
      console.error('API Error:', {
        status: error.response.status,
        statusText: error.response.statusText,
        url: error.config?.url,
        data: error.response.data
      });
    }

    return Promise.reject(error);
  }
);

export default api;
